import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DetailPageHeader,
  HairlineCard,
  Icons,
  iconSize,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../../components/AppShell';
import { useScope } from '../../providers/ScopeProvider';
import { useMutationToast } from '../../lib/mutation-toast';
import { useConfirmAction } from '../../lib/useConfirmAction';
import {
  openContractDocument,
  useContract,
  useDeleteContractDocument,
  useUpdateContract,
  useUploadContractDocument,
} from '../../lib/administration-api';

/**
 * Editing one contract's record, at `/administration/contracts/:id/edit`.
 *
 * Status is deliberately not on this page. A municipality moves a contract by
 * *acts* — submit it to council, sign it against a resolution, suspend it,
 * terminate it — each with its own permission and its own record of the
 * authority behind it, and those are the buttons on the list. What is left here
 * is the record: the reference, the term, the notes, and the copy that was
 * actually signed.
 */
export function ContractEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useMutationToast();
  const confirm = useConfirmAction();
  const { id } = useParams<{ id: string }>();
  const { active, can } = useScope();
  const localGovernmentId = active?.kind === 'local_government' ? active.id : undefined;

  const contract = useContract(id, localGovernmentId);
  const row = contract.data;

  const update = useUpdateContract();
  const uploadDocument = useUploadContractDocument();
  const deleteDocument = useDeleteContractDocument();

  const [draft, setDraft] = useState({ reference: '', startsOn: '', endsOn: '', notes: '' });
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!row) return;
    setDraft({
      reference: row.reference,
      startsOn: row.startsOn ?? '',
      endsOn: row.endsOn ?? '',
      notes: row.notes,
    });
  }, [row]);

  const run = async (successKey: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      toast.success(successKey);
      return true;
    } catch (err) {
      toast.error(err, 'gov.admin.errors.actionFailed');
      return false;
    }
  };

  const detailPath = `/administration/contracts/${id}`;

  return (
    <AppShell>
      <Stack spacing={2.5}>
        <DetailPageHeader
          backLabel={t('common.actions.back')}
          onBack={() => navigate(detailPath)}
          title={t('gov.admin.contracts.editTitle')}
          subtitle={row ? `${row.companyName} — ${row.localGovernmentName}` : undefined}
        />

        {contract.isLoading && (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!contract.isLoading && !row && (
          <HairlineCard sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('gov.admin.contracts.notFound')}
            </Typography>
          </HairlineCard>
        )}

        {row && (
          <>
            <Alert severity="info">{t('gov.admin.contracts.editHelp')}</Alert>

            <HairlineCard sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('gov.admin.contracts.termsSection')}
              </Typography>
              <Stack spacing={2.5}>
                <TextField
                  label={t('gov.admin.contracts.reference')}
                  value={draft.reference}
                  onChange={(e) => setDraft((d) => ({ ...d, reference: e.target.value }))}
                  fullWidth
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    type="date"
                    label={t('gov.admin.contracts.startsOn')}
                    value={draft.startsOn}
                    onChange={(e) => setDraft((d) => ({ ...d, startsOn: e.target.value }))}
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    type="date"
                    label={t('gov.admin.contracts.endsOn')}
                    value={draft.endsOn}
                    onChange={(e) => setDraft((d) => ({ ...d, endsOn: e.target.value }))}
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    helperText={t('gov.admin.contracts.endsOnHelp')}
                  />
                </Stack>
                <TextField
                  label={t('gov.admin.contracts.notes')}
                  value={draft.notes}
                  onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Stack>
            </HairlineCard>

            <HairlineCard sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('gov.admin.contracts.document')}
              </Typography>
              <Stack spacing={2}>
                {row.documentFilename ? (
                  <Stack
                    direction="row"
                    spacing={1.5}
                    useFlexGap
                    sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <Icons.FileText size={iconSize.md} aria-hidden />
                    <Typography
                      variant="body2"
                      sx={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}
                    >
                      {row.documentFilename}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<Icons.Download size={iconSize.md} />}
                      onClick={() => {
                        void openContractDocument(row.id, row.localGovernmentId).catch((err) =>
                          toast.error(err, 'gov.admin.contracts.documentFailed'),
                        );
                      }}
                    >
                      {t('common.actions.view')}
                    </Button>
                    {can('contract:draft') && (
                      <Button
                        size="small"
                        color="error"
                        disabled={deleteDocument.isPending}
                        onClick={async () => {
                          if (
                            !(await confirm({
                              title: t('gov.admin.contracts.documentRemoveTitle'),
                              body: t('gov.admin.contracts.documentRemoveBody', {
                                filename: row.documentFilename,
                              }),
                              confirmLabel: t('common.actions.delete'),
                              tone: 'danger',
                            }))
                          ) {
                            return;
                          }
                          await run('gov.admin.contracts.documentRemoved', () =>
                            deleteDocument.mutateAsync({
                              id: row.id,
                              localGovernmentId: row.localGovernmentId,
                            }),
                          );
                        }}
                      >
                        {t('common.actions.delete')}
                      </Button>
                    )}
                  </Stack>
                ) : (
                  <Alert severity="info">{t('gov.admin.contracts.noDocumentHelp')}</Alert>
                )}
                {can('contract:draft') && (
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<Icons.Upload size={iconSize.md} />}
                      disabled={uploadDocument.isPending}
                      onClick={() => fileInput.current?.click()}
                    >
                      {row.documentFilename
                        ? t('gov.admin.contracts.replaceDocument')
                        : t('gov.admin.contracts.uploadDocument')}
                    </Button>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.75 }}
                    >
                      {t('gov.admin.contracts.uploadHelp')}
                    </Typography>
                  </Box>
                )}
                <input
                  ref={fileInput}
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    // Cleared before the upload starts, so picking the same file
                    // again after a failure still fires a change event.
                    e.target.value = '';
                    if (!file) return;
                    void run('gov.admin.contracts.documentUploaded', () =>
                      uploadDocument.mutateAsync({
                        id: row.id,
                        localGovernmentId: row.localGovernmentId,
                        file,
                      }),
                    );
                  }}
                />
              </Stack>
            </HairlineCard>

            <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
              <Button variant="text" onClick={() => navigate(detailPath)}>
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="contained"
                disabled={update.isPending}
                onClick={async () => {
                  const saved = await run('gov.admin.contracts.saved', () =>
                    update.mutateAsync({
                      id: row.id,
                      body: {
                        localGovernmentId: row.localGovernmentId,
                        reference: draft.reference,
                        // Empty means "leave it": an omitted optional cannot say
                        // "clear this", and clearing a term is not offered here.
                        startsOn: draft.startsOn || undefined,
                        endsOn: draft.endsOn || undefined,
                        notes: draft.notes,
                      },
                    }),
                  );
                  if (saved) navigate(detailPath);
                }}
              >
                {t('common.actions.save')}
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </AppShell>
  );
}
